import { useInfiniteNotifications } from '@aibees/elephant/hooks/use-infinite-notifications';
import { SearchItemUnionProps } from '@aibees/elephant/types';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  ActionButtonContextContextData as ContextData,
  ActionButtonContextContextProps as ContextProps,
  ProcessingItemInfo,
  ProcessingItemState,
} from './types';

const context = createContext<ContextData>({} as ContextData);

export const { Provider } = context;

export const useActionButtonContext = () => {
  const ctx = useContext(context);
  if (!ctx)
    throw new Error(
      'useActionButtonContext must be used within ActionButtonProvider'
    );
  return ctx;
};

export const ActionButtonContextProvider = ({ children }: ContextProps) => {
  useInfiniteNotifications();
  const [item, setItem] = useState<SearchItemUnionProps | undefined>();
  const [processingItems, setProcessingItems] = useState<string[]>();

  const addProcessingItems = useCallback((info: ProcessingItemInfo) => {
    setProcessingItems((prevItems) => {
      const newItem = `${info.id}/${info.type}/${info.state}`;
      if (!prevItems) return [newItem];
      return [...prevItems, ...[newItem]];
    });
  }, []);

  const removeProcessingItems = useCallback(
    (info: Omit<ProcessingItemInfo, 'state'>) => {
      setProcessingItems((prevItems) => {
        if (!prevItems) return prevItems;
        const newItemWithoutState = `${info.id}/${info.type}`;
        return prevItems.filter((x) => !x.includes(newItemWithoutState));
      });
    },
    []
  );

  const isItemProcessing = useCallback(
    (info: Omit<ProcessingItemInfo, 'state'>) => {
      const itemWithoutState = `${info.id}/${info.type}`;
      if (!processingItems || !info.id) return false;
      return processingItems.some((x) => x.includes(itemWithoutState));
    },
    [processingItems]
  );

  const getItemState = useCallback(
    (info: Omit<ProcessingItemInfo, 'state'>) => {
      const itemWithoutState = `${info.id}/${info.type}`;
      if (!processingItems || !info.id) return undefined;
      const itemDraft = processingItems.find((x) =>
        x.includes(itemWithoutState)
      );
      if (!itemDraft) return undefined;
      const state = itemDraft.split('/')[2];
      return state as ProcessingItemState;
    },
    [processingItems]
  );

  const contextValue = useMemo(
    () => ({
      item,
      setItem,
      processingItems,
      addProcessingItems,
      removeProcessingItems,
      isItemProcessing,
      getItemState,
    }),
    [
      item,
      setItem,
      processingItems,
      addProcessingItems,
      removeProcessingItems,
      isItemProcessing,
      getItemState,
    ]
  );

  return <Provider value={contextValue}>{children}</Provider>;
};
